import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFamilyContext } from '../../../contexts/FamilyContext';
import { usePacking } from '../../../hooks/usePacking';
import { format, isAfter, isBefore, differenceInDays } from 'date-fns';
import { 
  X, Heart, Share, MapPin, Calendar, Users, DollarSign, Clock,
  Plane, Car, Ship, Train, ChevronLeft, ChevronRight, Plus,
  Edit3, Check, AlertCircle, Package, FileText, Cloud, Camera,
  Star, Sparkles, ArrowRight, Vote, Home, Ticket, Menu,
  Globe, Phone, Info, CheckCircle, UserCheck, ExternalLink,
  Maximize2, Sun, Umbrella, Thermometer, Wind
} from 'lucide-react';
import TripBudgetCalculator from './TripBudgetCalculator';
import AvatarOrInitial from '../../Family/AvatarOrInitial';

export default function TripDetailPanel({ 
  trip, 
  isOpen, 
  onClose, 
  onEdit,
  originElement 
}) {
  const navigate = useNavigate();
  const { currentUser, family, updateFamilyActivity, voteOnFamilyActivity } = useFamilyContext();
  const { lists: allPackingLists } = usePacking();
  const [activeTab, setActiveTab] = useState('overview');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showInterestConfirm, setShowInterestConfirm] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [relatedPackingLists, setRelatedPackingLists] = useState([]);
  const [showLightbox, setShowLightbox] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const panelRef = useRef(null);
  const backdropRef = useRef(null);
  
  // Extract trip metadata
  const metadata = trip?.trip_metadata || {};
  const images = [
    metadata.images?.main,
    metadata.images?.secondary,
    metadata.images?.tertiary
  ].filter(Boolean);
  
  // Get family members
  const familyMembers = [...(family?.parents || []), ...(family?.kids || [])];
  const interestedMembers = metadata.interested_members || [];
  const isInterested = interestedMembers.includes(currentUser?.id);
  
  // Get trip phase info
  const tripPhase = metadata.trip_phase || 'idea';
  const phaseConfig = {
    idea: { label: '💡 Idea', color: 'azure', next: 'researching' },
    researching: { label: '🔍 Researching', color: 'amber', next: 'planning' },
    planning: { label: '📋 Planning', color: 'violet', next: 'booked' },
    booked: { label: '✅ Booked', color: 'emerald', next: 'completed' },
    completed: { label: '🎉 Completed', color: 'stone', next: null }
  };
  
  // Get phase color helper
  const getPhaseColor = () => {
    const colors = {
      idea: 'bg-azure-100 text-azure-800',
      researching: 'bg-amber-100 text-amber-800',
      planning: 'bg-violet-100 text-violet-800',
      booked: 'bg-emerald-100 text-emerald-800',
      completed: 'bg-stone-100 text-stone-800'
    };
    return colors[tripPhase] || 'bg-stone-100 text-stone-800';
  };
  
  // Get phase display helper
  const getPhaseDisplay = () => {
    return phaseConfig[tripPhase]?.label || '💡 Idea';
  };
  
  // Handle opening animation
  useEffect(() => {
    if (isOpen && originElement && panelRef.current) {
      setIsAnimating(true);
      document.body.style.overflow = 'hidden';
      
      const originRect = originElement.getBoundingClientRect();
      const panel = panelRef.current;
      
      // Set initial position and scale
      panel.style.transition = 'none';
      panel.style.position = 'fixed';
      panel.style.top = '0px';
      panel.style.left = '0px';
      panel.style.width = '100vw';
      panel.style.height = '100vh';
      panel.style.transformOrigin = `${originRect.left + originRect.width/2}px ${originRect.top + originRect.height/2}px`;
      
      const scaleX = originRect.width / window.innerWidth;
      const scaleY = originRect.height / window.innerHeight;
      const scale = Math.min(scaleX, scaleY);
      panel.style.transform = `scale(${scale})`;
      
      // Animate to full size
      requestAnimationFrame(() => {
        if (backdropRef.current) {
          backdropRef.current.style.opacity = '1';
        }
        panel.style.transition = 'transform 0.4s cubic-bezier(0.25, 0.8, 0.25, 1)';
        panel.style.transform = 'scale(1)';
        
        setTimeout(() => {
          setIsAnimating(false);
        }, 400);
      });
    }
    
    return () => {
      if (isOpen) {
        document.body.style.overflow = '';
      }
    };
  }, [isOpen, originElement]);
  
  // Fetch packing lists related to this trip
  useEffect(() => {
    if (trip && allPackingLists) {
      // Filter packing lists that are related to this trip
      const related = allPackingLists.filter(list => {
        // Check if list is linked to this trip by ID
        if (metadata.packing_list_ids?.includes(list.id)) return true;
        
        // Check if list destination matches trip location
        if (list.destination && trip.location && 
            list.destination.toLowerCase().includes(trip.location.toLowerCase())) return true;
        
        // Check if list dates overlap with trip dates
        if (list.start_date && trip.start_date) {
          const listStart = new Date(list.start_date);
          const tripStart = new Date(trip.start_date);
          const daysDiff = Math.abs(listStart - tripStart) / (1000 * 60 * 60 * 24);
          if (daysDiff <= 7) return true; // Within a week of trip
        }
        
        return false;
      });
      
      setRelatedPackingLists(related);
    }
  }, [trip, allPackingLists, metadata.packing_list_ids]);
  
  // Handle interest toggle
  const handleInterestToggle = async () => {
    if (!currentUser) return;
    
    const updatedMembers = isInterested
      ? interestedMembers.filter(id => id !== currentUser.id)
      : [...interestedMembers, currentUser.id];
    
    try {
      const updatedTrip = await updateFamilyActivity(trip.id, {
        trip_metadata: {
          ...metadata,
          interested_members: updatedMembers
        }
      });
      
      // Update the local trip object to reflect changes immediately
      if (updatedTrip) {
        trip.trip_metadata = {
          ...trip.trip_metadata,
          interested_members: updatedMembers
        };
      }
      
      // Show confirmation if newly interested
      if (!isInterested) {
        setShowInterestConfirm(true);
        setTimeout(() => setShowInterestConfirm(false), 2000);
      }
    } catch (error) {
      console.error('Error updating interest:', error);
      alert('Failed to update interest. Please try again.');
    }
  };
  
  // Handle phase progression
  const handlePhaseProgression = async () => {
    const nextPhase = phaseConfig[tripPhase].next;
    if (!nextPhase) return;
    
    // Validate requirements for next phase
    const validation = validatePhaseRequirements(nextPhase);
    if (!validation.isValid) {
      alert(validation.message || `Please complete required information before moving to ${phaseConfig[nextPhase].label} phase.`);
      return;
    }
    
    try {
      const updatedTrip = await updateFamilyActivity(trip.id, {
        trip_metadata: {
          ...metadata,
          trip_phase: nextPhase,
          phase_updated_at: new Date().toISOString(),
          phase_updated_by: currentUser?.id
        }
      });
      
      // Update local trip object
      if (updatedTrip) {
        trip.trip_metadata = {
          ...trip.trip_metadata,
          trip_phase: nextPhase,
          phase_updated_at: new Date().toISOString(),
          phase_updated_by: currentUser?.id
        };
      }
      
      // Show success message
      alert(`Trip successfully moved to ${phaseConfig[nextPhase].label} phase!`);
    } catch (error) {
      console.error('Error updating phase:', error);
      alert('Failed to update trip phase. Please try again.');
    }
  };
  
  // Validate phase requirements
  const validatePhaseRequirements = (targetPhase) => {
    switch (targetPhase) {
      case 'researching':
        // Must have at least 2 interested members
        if (interestedMembers.length < 2) {
          return {
            isValid: false,
            message: 'At least 2 family members must be interested before moving to Researching phase.'
          };
        }
        if (!trip.title || !trip.location) {
          return {
            isValid: false,
            message: 'Please add a title and location before moving to Researching phase.'
          };
        }
        return { isValid: true };
        
      case 'planning':
        // Must have budget estimate and some votes
        if (!metadata.budget_breakdown?.totals) {
          return {
            isValid: false,
            message: 'Please add a budget estimate before moving to Planning phase.'
          };
        }
        const voteCount = Object.keys(trip.votes || {}).length;
        if (voteCount < 2) {
          return {
            isValid: false,
            message: 'At least 2 family members must vote before moving to Planning phase.'
          };
        }
        return { isValid: true };
        
      case 'booked':
        // Must have dates and accommodations
        if (!trip.start_date || !trip.end_date) {
          return {
            isValid: false,
            message: 'Please set trip dates before marking as Booked.'
          };
        }
        if (!metadata.accommodations || metadata.accommodations.length === 0) {
          return {
            isValid: false,
            message: 'Please add accommodation details before marking as Booked.'
          };
        }
        return { isValid: true };
        
      case 'completed':
        // Trip date must be in the past
        if (!trip.end_date) {
          return {
            isValid: false,
            message: 'Trip must have an end date to mark as Completed.'
          };
        }
        if (new Date(trip.end_date) > new Date()) {
          return {
            isValid: false,
            message: 'Trip must be in the past to mark as Completed.'
          };
        }
        return { isValid: true };
        
      default:
        return { isValid: true };
    }
  };
  
  // Create packing list from trip
  const handleCreatePackingList = () => {
    navigate('/packing/new', {
      state: {
        tripId: trip.id,
        name: `${trip.title} - Packing List`,
        destination: trip.location,
        start_date: trip.start_date,
        end_date: trip.end_date,
        weather_location: trip.location,
        trip_type: metadata.trip_features?.includes('international') ? 'international' : 'domestic',
        activities: metadata.trip_features
      }
    });
  };
  
  // Get transport icon
  const getTransportIcon = () => {
    const iconClass = "w-5 h-5";
    switch (metadata.transport_type) {
      case 'fly': return <Plane className={iconClass} />;
      case 'drive': return <Car className={iconClass} />;
      case 'cruise': return <Ship className={iconClass} />;
      case 'train': return <Train className={iconClass} />;
      default: return <Car className={iconClass} />;
    }
  };
  
  // Format duration
  const formatDuration = () => {
    const durations = {
      'overnight': 'Overnight',
      'weekend': '2-3 days',
      '4-7days': '4-7 days',
      '1-2weeks': '1-2 weeks',
      '2+weeks': '2+ weeks'
    };
    return durations[metadata.duration_category] || 'TBD';
  };
  
  // Calculate budget totals
  const calculateBudgetTotals = () => {
    if (!metadata.budget_breakdown?.totals) return { per_person: 0, per_person_max: 0, trip_total: 0 };
    return metadata.budget_breakdown.totals;
  };
  
  const budgetTotals = calculateBudgetTotals();
  
  // Handle close with animation
  const handleClose = () => {
    if (originElement && panelRef.current) {
      const panel = panelRef.current;
      const originRect = originElement.getBoundingClientRect();
      
      if (backdropRef.current) {
        backdropRef.current.style.opacity = '0';
      }
      
      panel.style.transformOrigin = `${originRect.left + originRect.width/2}px ${originRect.top + originRect.height/2}px`;
      panel.style.transition = 'transform 0.3s cubic-bezier(0.5, 0, 0.3, 1)';
      
      const scaleX = originRect.width / window.innerWidth;
      const scaleY = originRect.height / window.innerHeight;
      const scale = Math.min(scaleX, scaleY);
      panel.style.transform = `scale(${scale})`;
      
      setTimeout(() => {
        document.body.style.overflow = '';
        onClose();
      }, 300);
    } else {
      document.body.style.overflow = '';
      onClose();
    }
  };
  
  if (!isOpen || !trip) return null;
  
  return (
    <div className="fixed inset-0 z-50">
      <div 
        ref={backdropRef}
        className="absolute inset-0 bg-black transition-opacity duration-300"
        style={{ opacity: 0 }}
        onClick={handleClose}
      />
      
      <div 
        ref={panelRef}
        className="absolute inset-0 bg-white flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="px-4 py-3 border-b border-stone-200 flex items-center justify-between bg-white flex-shrink-0">
          <button
            onClick={handleClose}
            className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="flex items-center gap-2">
            <button className="p-2 hover:bg-stone-100 rounded-lg transition-colors">
              <Share className="w-5 h-5" />
            </button>
            <button 
              onClick={() => {/* TODO: Toggle favorite */}}
              className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
            >
              <Heart className="w-5 h-5" />
            </button>
            <button
              onClick={() => {
                handleClose();
                setTimeout(() => onEdit?.(trip), 300);
              }}
              className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
            >
              <Edit3 className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Scrollable Content Container */}
        <div className="flex-1 overflow-y-auto bg-stone-50">
          {/* Phase Status Bar */}
          <div className="px-4 py-3 bg-gradient-to-r from-stone-50 to-stone-100 sticky top-0 z-10">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-stone-600">Trip Progress</span>
              {phaseConfig[tripPhase].next && (
                <button
                  onClick={handlePhaseProgression}
                  className="text-sm px-3 py-1 bg-white border border-stone-200 rounded-lg hover:bg-stone-50 transition-colors flex items-center gap-1"
                >
                  Move to {phaseConfig[phaseConfig[tripPhase].next].label}
                  <ArrowRight className="w-3 h-3" />
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              {Object.entries(phaseConfig).map(([phase, config], index) => (
                <React.Fragment key={phase}>
                  <div className={`flex items-center gap-2 ${
                    phase === tripPhase ? 'opacity-100' : 'opacity-50'
                  }`}>
                    <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      phase === tripPhase 
                        ? `bg-${config.color}-100 text-${config.color}-800`
                        : 'bg-stone-100 text-stone-600'
                    }`}>
                      {config.label}
                    </div>
                  </div>
                  {index < Object.keys(phaseConfig).length - 1 && (
                    <div className={`flex-1 h-0.5 ${
                      Object.keys(phaseConfig).indexOf(tripPhase) > index
                        ? 'bg-emerald-500'
                        : 'bg-stone-300'
                    }`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
          
          {/* Main Content */}
          <div>
            {/* Hero Section - Magazine Style */}
            {images.length > 0 ? (
              <div className="relative">
                {/* Desktop: Multi-image hero */}
                <div className="hidden md:block">
                  {images.length >= 3 ? (
                    // 3-image magazine layout
                    <div className="grid grid-cols-2 h-fit-content">
                    <div 
                      className="relative overflow-hidden cursor-pointer"
                      onClick={() => { setLightboxIndex(0); setShowLightbox(true); }}
                    >
                      <img
                        src={images[0]}
                        alt={trip.title}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                      <button className="absolute top-4 right-4 p-2 bg-white/80 backdrop-blur-sm rounded-full hover:bg-white transition-colors">
                        <Maximize2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="grid grid-rows-2">
                      <div 
                        className="relative overflow-hidden cursor-pointer"
                        onClick={() => { setLightboxIndex(1); setShowLightbox(true); }}
                      >
                        <img
                          src={images[1]}
                          alt={`${trip.title} 2`}
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
                      </div>
                      <div 
                        className="relative overflow-hidden cursor-pointer"
                        onClick={() => { setLightboxIndex(2); setShowLightbox(true); }}
                      >
                        <img
                          src={images[2]}
                          alt={`${trip.title} 3`}
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
                      </div>
                    </div>
                  </div>
                ) : images.length === 2 ? (
                  // 2-image split layout
                  <div className="grid grid-cols-2 h-[400px]">
                    {images.map((img, idx) => (
                      <div 
                        key={idx}
                        className="relative overflow-hidden cursor-pointer"
                        onClick={() => { setLightboxIndex(idx); setShowLightbox(true); }}
                      >
                        <img
                          src={img}
                          alt={`${trip.title} ${idx + 1}`}
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                        <button className="absolute top-4 right-4 p-2 bg-white/80 backdrop-blur-sm rounded-full hover:bg-white transition-colors">
                          <Maximize2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  // Single large image
                  <div 
                    className="relative h-[400px] overflow-hidden cursor-pointer"
                    onClick={() => { setLightboxIndex(0); setShowLightbox(true); }}
                  >
                    <img
                      src={images[0]}
                      alt={trip.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                    <button className="absolute top-4 right-4 p-2 bg-white/80 backdrop-blur-sm rounded-full hover:bg-white transition-colors">
                      <Maximize2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
                
                {/* Hero Content Overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                  <div className="max-w-6xl mx-auto">
                    <div className="flex items-center gap-3 mb-3">
                      <span className={`px-3 py-1.5 rounded-full text-xs font-bold backdrop-blur-sm ${
                        getPhaseColor().replace('text-', 'text-white bg-').replace('-800', '-600/80')
                      }`}>
                        {getPhaseDisplay()}
                      </span>
                      {metadata.trip_features?.slice(0, 3).map(feature => {
                        const featureEmojis = {
                          'domestic': '🇺🇸',
                          'international': '🌍',
                          'coastal': '🏖️',
                          'mountains': '⛰️',
                          'desert': '🏜️',
                          'city': '🏙️',
                          'theme_parks': '🎢',
                          'national_parks': '🏞️'
                        };
                        return featureEmojis[feature] ? (
                          <span key={feature} className="text-2xl">{featureEmojis[feature]}</span>
                        ) : null;
                      })}
                    </div>
                    <h1 className="text-4xl md:text-5xl font-bold mb-3 drop-shadow-lg">
                      {trip.title}
                    </h1>
                    {trip.location && (
                      <div className="flex items-center gap-2 text-lg">
                        <MapPin className="w-5 h-5" />
                        <span>{trip.location}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Mobile: Single image carousel */}
              <div className="md:hidden relative h-64 bg-stone-100">
                <img
                  src={images[currentImageIndex]}
                  alt={trip.title}
                  className="w-full h-full object-cover"
                />
                {images.length > 1 && (
                  <>
                    <button
                      onClick={() => setCurrentImageIndex((prev) => 
                        prev === 0 ? images.length - 1 : prev - 1
                      )}
                      className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-white/80 rounded-full hover:bg-white transition-colors"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setCurrentImageIndex((prev) => 
                        (prev + 1) % images.length
                      )}
                      className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-white/80 rounded-full hover:bg-white transition-colors"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                      {images.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentImageIndex(index)}
                          className={`w-2 h-2 rounded-full transition-colors ${
                            index === currentImageIndex ? 'bg-white' : 'bg-white/50'
                          }`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className="h-64 md:h-[400px] bg-gradient-to-br from-azure-100 via-teal-100 to-emerald-100 flex items-center justify-center">
              <div className="text-center">
                <div className="text-8xl mb-4 animate-pulse">✈️</div>
                <p className="text-stone-600 text-lg">No images yet</p>
                <button
                  onClick={() => onEdit?.(trip)}
                  className="mt-4 px-6 py-2 bg-white/80 backdrop-blur-sm rounded-lg hover:bg-white transition-colors text-stone-700"
                >
                  Add Photos
                </button>
              </div>
            </div>
          )}
          
          {/* Main Content Container */}
          <div className="bg-white">
            {/* Quick Stats Bar - Magazine Style */}
            <div className="border-y border-stone-200 bg-gradient-to-r from-stone-50 to-white">
              <div className="max-w-6xl mx-auto px-4 md:px-6 py-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {/* Transport & Duration */}
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-azure-100 rounded-xl">
                      {getTransportIcon()}
                    </div>
                    <div>
                      <p className="text-xs text-stone-500 uppercase tracking-wide">Travel</p>
                      <p className="font-semibold text-stone-900 capitalize">{metadata.transport_type || 'TBD'}</p>
                      {metadata.travel_time && (
                        <p className="text-xs text-stone-600">{metadata.travel_time}</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Duration */}
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-violet-100 rounded-xl">
                      <Clock className="w-5 h-5 text-violet-600" />
                    </div>
                    <div>
                      <p className="text-xs text-stone-500 uppercase tracking-wide">Duration</p>
                      <p className="font-semibold text-stone-900">{formatDuration()}</p>
                    </div>
                  </div>
                  
                  {/* Budget */}
                  {budgetTotals.per_person > 0 && (
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-gold-100 rounded-xl">
                        <DollarSign className="w-5 h-5 text-gold-600" />
                      </div>
                      <div>
                        <p className="text-xs text-stone-500 uppercase tracking-wide">Per Person</p>
                        <p className="font-semibold text-stone-900">
                          ${budgetTotals.per_person}
                          {budgetTotals.per_person_max && budgetTotals.per_person_max !== budgetTotals.per_person ? 
                            `-${budgetTotals.per_person_max}` : ''}
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {/* Season */}
                  {metadata.preferred_season && (
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-emerald-100 rounded-xl">
                        <Sun className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-xs text-stone-500 uppercase tracking-wide">Best Time</p>
                        <p className="font-semibold text-stone-900">{metadata.preferred_season}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Mobile Title (shown below hero on mobile) */}
            <div className="md:hidden p-4">
              <h1 className="text-2xl font-bold text-stone-900 mb-2">
                {trip.title}
              </h1>
              {trip.location && (
                <div className="flex items-center gap-2 text-stone-600">
                  <MapPin className="w-4 h-4" />
                  <span>{trip.location}</span>
                </div>
              )}
            </div>
            
            <div className="max-w-6xl mx-auto p-4 md:p-6">
            
            {/* Interest Section (for Idea phase) */}
            {tripPhase === 'idea' && (
              <div className="mb-6 p-4 bg-azure-50 border border-azure-200 rounded-xl">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-azure-900">Who's Interested?</h3>
                  <span className="text-sm text-azure-700">
                    {interestedMembers.length}/{familyMembers.length} interested
                  </span>
                </div>
                
                <div className="flex flex-wrap gap-2 mb-4">
                  {familyMembers.map(member => {
                    const memberInterested = interestedMembers.includes(member.id);
                    return (
                      <div
                        key={member.id}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
                          memberInterested
                            ? 'bg-azure-500 text-white'
                            : 'bg-white text-stone-700 border border-stone-200'
                        }`}
                      >
                        <AvatarOrInitial
                          avatar={member.avatar}
                          name={member.name}
                          size="xs"
                        />
                        <span className="text-sm font-medium">{member.name}</span>
                        {memberInterested && <Check className="w-4 h-4" />}
                      </div>
                    );
                  })}
                </div>
                
                <button
                  onClick={handleInterestToggle}
                  className={`w-full py-3 rounded-lg font-semibold transition-all ${
                    isInterested
                      ? 'bg-white text-azure-700 border-2 border-azure-500'
                      : 'bg-azure-500 text-white hover:bg-azure-600'
                  }`}
                >
                  {isInterested ? "I'm Interested ✓" : "Mark Me as Interested"}
                </button>
                
                {showInterestConfirm && (
                  <div className="mt-2 p-2 bg-emerald-100 text-emerald-800 rounded-lg text-sm text-center">
                    Great! You're marked as interested in this trip!
                  </div>
                )}
                
                {interestedMembers.length >= Math.ceil(familyMembers.length / 2) && (
                  <div className="mt-3 p-3 bg-amber-100 border border-amber-300 rounded-lg">
                    <p className="text-sm text-amber-900">
                      <strong>Ready for voting!</strong> Enough family members are interested. 
                      Move to "Researching" phase to open family voting.
                    </p>
                  </div>
                )}
              </div>
            )}
            
            {/* Tab Navigation */}
            <div className="border-b border-stone-200 mb-6">
              <div className="flex gap-4 overflow-x-auto">
                {['overview', 'accommodations', 'activities', 'budget', 'packing', 'documents'].map(tab => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-4 py-3 font-medium capitalize whitespace-nowrap transition-all ${
                      activeTab === tab
                        ? 'text-gold-600 border-b-2 border-gold-600'
                        : 'text-stone-600 hover:text-stone-900'
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Tab Content - Magazine Layout */}
            <div className="min-h-[300px]">
              {activeTab === 'overview' && (
                <div className="space-y-8">
                  {/* Magazine-style Description Section */}
                  {trip.description && (
                    <div className="md:grid md:grid-cols-3 md:gap-8">
                      <div className="md:col-span-2">
                        <h3 className="text-2xl font-serif text-stone-900 mb-4">About This Adventure</h3>
                        <p className="text-lg leading-relaxed text-stone-700 first-letter:text-5xl first-letter:font-bold first-letter:mr-1 first-letter:float-left">
                          {trip.description}
                        </p>
                      </div>
                      <div className="mt-6 md:mt-0">
                        {/* Trip Highlights Card */}
                        <div className="bg-gradient-to-br from-gold-50 to-amber-50 rounded-2xl p-6 border border-gold-200">
                          <h4 className="font-bold text-stone-900 mb-4 flex items-center gap-2">
                            <Sparkles className="w-5 h-5 text-gold-600" />
                            Trip Highlights
                          </h4>
                          {metadata.trip_features?.length > 0 ? (
                            <div className="space-y-3">
                              {metadata.trip_features.slice(0, 5).map(feature => {
                                const featureInfo = {
                                  'domestic': { emoji: '🇺🇸', label: 'Domestic Travel' },
                                  'international': { emoji: '🌍', label: 'International' },
                                  'coastal': { emoji: '🏖️', label: 'Beach & Coast' },
                                  'mountains': { emoji: '⛰️', label: 'Mountain Views' },
                                  'desert': { emoji: '🏜️', label: 'Desert Landscape' },
                                  'city': { emoji: '🏙️', label: 'City Experience' },
                                  'theme_parks': { emoji: '🎢', label: 'Theme Parks' },
                                  'national_parks': { emoji: '🏞️', label: 'National Parks' },
                                  'resort': { emoji: '🏨', label: 'Resort Stay' },
                                  'adventure': { emoji: '🎯', label: 'Adventure' },
                                  'relaxation': { emoji: '🧘', label: 'Relaxation' },
                                  'cultural': { emoji: '🎭', label: 'Cultural' },
                                  'food_focused': { emoji: '🍽️', label: 'Culinary' }
                                };
                                const info = featureInfo[feature] || { emoji: '✨', label: feature };
                                return (
                                  <div key={feature} className="flex items-center gap-3">
                                    <span className="text-2xl">{info.emoji}</span>
                                    <span className="text-sm font-medium text-stone-700">{info.label}</span>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <p className="text-sm text-stone-600">Add trip features to highlight</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Travel Timing - Card Style */}
                  {(trip.start_date || metadata.preferred_season) && (
                    <div className="bg-gradient-to-r from-azure-50 to-indigo-50 rounded-2xl p-6 border border-azure-200">
                      <h3 className="text-xl font-bold text-stone-900 mb-4 flex items-center gap-2">
                        <Calendar className="w-6 h-6 text-azure-600" />
                        Travel Timing
                      </h3>
                      <div className="grid md:grid-cols-2 gap-6">
                        {trip.start_date ? (
                          <div>
                            <p className="text-sm text-stone-600 mb-1">Trip Dates</p>
                            <p className="text-lg font-semibold text-stone-900">
                              {format(new Date(trip.start_date), 'MMMM d, yyyy')}
                              {trip.end_date && trip.end_date !== trip.start_date && (
                                <>
                                  <span className="text-stone-500 font-normal mx-2">to</span>
                                  {format(new Date(trip.end_date), 'MMMM d, yyyy')}
                                </>
                              )}
                            </p>
                          </div>
                        ) : metadata.preferred_season && (
                          <div>
                            <p className="text-sm text-stone-600 mb-1">Best Season</p>
                            <p className="text-lg font-semibold text-stone-900">{metadata.preferred_season}</p>
                            {metadata.preferred_months?.length > 0 && (
                              <p className="text-sm text-stone-600 mt-1">
                                {metadata.preferred_months.join(', ')}
                              </p>
                            )}
                          </div>
                        )}
                        
                        {/* Weather Preview (placeholder for future feature) */}
                        <div>
                          <p className="text-sm text-stone-600 mb-1">Expected Weather</p>
                          <div className="flex items-center gap-4">
                            <div className="flex items-center gap-1">
                              <Thermometer className="w-4 h-4 text-orange-500" />
                              <span className="text-sm">75-85°F</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Sun className="w-4 h-4 text-yellow-500" />
                              <span className="text-sm">Sunny</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* International Requirements */}
                  {metadata.trip_features?.includes('international') && (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <h4 className="font-semibold text-amber-900 mb-2 flex items-center gap-2">
                        <Globe className="w-5 h-5" />
                        International Travel Requirements
                      </h4>
                      <ul className="space-y-1 text-sm text-amber-800">
                        {metadata.passport_required && (
                          <li className="flex items-center gap-2">
                            <Check className="w-4 h-4" />
                            Passports required for all travelers
                          </li>
                        )}
                        {metadata.visa_required && (
                          <li className="flex items-center gap-2">
                            <Check className="w-4 h-4" />
                            Visas may be required (check requirements)
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              )}
              
              {activeTab === 'accommodations' && (
                <div className="space-y-6">
                  {/* Magazine-style header */}
                  <div className="border-b border-stone-200 pb-4">
                    <h3 className="text-2xl font-serif text-stone-900">Where to Stay</h3>
                    <p className="text-sm text-stone-600 mt-1">Potential lodging options for the trip</p>
                  </div>
                  
                  {metadata.accommodations?.length > 0 ? (
                    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                      {metadata.accommodations.map((acc, index) => (
                        <div key={index} className="bg-white rounded-xl border border-stone-200 overflow-hidden hover:shadow-lg transition-shadow">
                          {/* Image Gallery Strip */}
                          {acc.images?.length > 0 ? (
                            <div className="relative h-32 bg-stone-100">
                              {acc.images.length === 1 ? (
                                <img
                                  src={acc.images[0]}
                                  alt={acc.name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="flex h-full">
                                  {acc.images.slice(0, 3).map((img, idx) => (
                                    <div key={idx} className={`relative ${idx === 0 ? 'w-1/2' : 'w-1/4'} h-full`}>
                                      <img
                                        src={img}
                                        alt={`${acc.name} ${idx + 1}`}
                                        className="w-full h-full object-cover"
                                      />
                                      {idx > 0 && <div className="absolute inset-0 bg-gradient-to-r from-white/10 via-transparent to-transparent" />}
                                    </div>
                                  ))}
                                </div>
                              )}
                              {/* Type Badge */}
                              <div className="absolute top-2 left-2">
                                <span className={`px-2 py-1 text-xs font-semibold rounded-full backdrop-blur-sm ${
                                  acc.type === 'hotel' ? 'bg-azure-100/90 text-azure-800' :
                                  acc.type === 'rental' ? 'bg-emerald-100/90 text-emerald-800' :
                                  'bg-violet-100/90 text-violet-800'
                                }`}>
                                  {acc.type === 'hotel' ? '🏨 Hotel' :
                                   acc.type === 'rental' ? '🏠 Rental' :
                                   '🏖️ Resort'}
                                </span>
                              </div>
                            </div>
                          ) : (
                            <div className="h-32 bg-gradient-to-br from-stone-100 to-stone-200 flex items-center justify-center">
                              <div className="text-4xl opacity-50">
                                {acc.type === 'hotel' ? '🏨' : acc.type === 'rental' ? '🏠' : '🏖️'}
                              </div>
                            </div>
                          )}
                          
                          {/* Content */}
                          <div className="p-4">
                            <h4 className="font-semibold text-stone-900 mb-1">
                              {acc.name || `${acc.type} Option ${index + 1}`}
                            </h4>
                            
                            {/* Price and Rooms */}
                            {(acc.price_per_night || acc.rooms_needed) && (
                              <div className="flex items-center gap-3 text-sm text-stone-600 mb-2">
                                {acc.price_per_night && (
                                  <span className="font-semibold text-gold-700">
                                    ${acc.price_per_night}/night
                                  </span>
                                )}
                                {acc.rooms_needed && (
                                  <span className="flex items-center gap-1">
                                    <Home className="w-3 h-3" />
                                    {acc.rooms_needed} {acc.rooms_needed === 1 ? 'room' : 'rooms'}
                                  </span>
                                )}
                              </div>
                            )}
                            
                            {/* Description */}
                            {acc.description && (
                              <p className="text-sm text-stone-600 mb-3 line-clamp-2">
                                {acc.description}
                              </p>
                            )}
                            
                            {/* View Details Link */}
                            {acc.url && (
                              <a
                                href={acc.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-sm text-azure-600 hover:text-azure-700 font-medium"
                              >
                                View Details
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Home className="w-16 h-16 mx-auto mb-4 text-stone-300" />
                      <p className="text-lg text-stone-600 mb-2">No accommodations added yet</p>
                      <p className="text-sm text-stone-500">Add lodging options when editing the trip</p>
                    </div>
                  )}
                </div>
              )}
              
              {activeTab === 'activities' && (
                <div className="space-y-6">
                  {/* Magazine-style header */}
                  <div className="border-b border-stone-200 pb-4">
                    <h3 className="text-2xl font-serif text-stone-900">Things to Do</h3>
                    <p className="text-sm text-stone-600 mt-1">Attractions and activities</p>
                  </div>
                  
                  {metadata.trip_activities?.length > 0 ? (
                    <div className="space-y-8">
                      {/* Must See Section */}
                      {metadata.trip_activities.filter(a => a.priority === 'must').length > 0 && (
                        <div>
                          <h4 className="text-lg font-semibold text-stone-900 mb-4 flex items-center gap-2">
                            <span className="text-orange-500">★</span> Must-See Attractions
                          </h4>
                          <div className="grid gap-4 md:grid-cols-2">
                            {metadata.trip_activities
                              .filter(a => a.priority === 'must')
                              .map((activity, index) => (
                                <div key={index} className="bg-white rounded-lg border border-orange-200 overflow-hidden hover:shadow-md transition-shadow">
                                  {/* Image Strip */}
                                  {activity.images?.length > 0 && (
                                    <div className="h-24 flex">
                                      {activity.images.slice(0, 3).map((img, idx) => (
                                        <div key={idx} className={`relative ${activity.images.length === 1 ? 'w-full' : 'w-1/3'} h-full`}>
                                          <img
                                            src={img}
                                            alt={`${activity.title} ${idx + 1}`}
                                            className="w-full h-full object-cover"
                                          />
                                          {idx > 0 && <div className="absolute inset-y-0 left-0 w-px bg-white" />}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  
                                  {/* Content */}
                                  <div className="p-4">
                                    <div className="flex items-start justify-between mb-2">
                                      <h5 className="font-semibold text-stone-900">{activity.title}</h5>
                                      {activity.cost_estimate > 0 && (
                                        <span className="px-2 py-1 bg-gold-100 text-gold-800 text-xs font-semibold rounded-full">
                                          ${activity.cost_estimate}
                                        </span>
                                      )}
                                    </div>
                                    {activity.description && (
                                      <p className="text-sm text-stone-600 line-clamp-2">
                                        {activity.description}
                                      </p>
                                    )}
                                    {activity.url && (
                                      <a
                                        href={activity.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1 text-sm text-azure-600 hover:text-azure-700 font-medium mt-2"
                                      >
                                        Learn More
                                        <ExternalLink className="w-3 h-3" />
                                      </a>
                                    )}
                                  </div>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Optional Section */}
                      {metadata.trip_activities.filter(a => a.priority === 'optional').length > 0 && (
                        <div>
                          <h4 className="text-lg font-semibold text-stone-900 mb-4 flex items-center gap-2">
                            <span className="text-azure-500">◆</span> If Time Allows
                          </h4>
                          <div className="grid gap-4 md:grid-cols-2">
                            {metadata.trip_activities
                              .filter(a => a.priority === 'optional')
                              .map((activity, index) => (
                                <div key={index} className="bg-white rounded-lg border border-azure-200 overflow-hidden hover:shadow-md transition-shadow">
                                  {/* Image Strip */}
                                  {activity.images?.length > 0 && (
                                    <div className="h-24 flex">
                                      {activity.images.slice(0, 3).map((img, idx) => (
                                        <div key={idx} className={`relative ${activity.images.length === 1 ? 'w-full' : 'w-1/3'} h-full`}>
                                          <img
                                            src={img}
                                            alt={`${activity.title} ${idx + 1}`}
                                            className="w-full h-full object-cover"
                                          />
                                          {idx > 0 && <div className="absolute inset-y-0 left-0 w-px bg-white" />}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  
                                  {/* Content */}
                                  <div className="p-4">
                                    <div className="flex items-start justify-between mb-2">
                                      <h5 className="font-semibold text-stone-900">{activity.title}</h5>
                                      {activity.cost_estimate > 0 && (
                                        <span className="px-2 py-1 bg-azure-100 text-azure-800 text-xs font-semibold rounded-full">
                                          ${activity.cost_estimate}
                                        </span>
                                      )}
                                    </div>
                                    {activity.description && (
                                      <p className="text-sm text-stone-600 line-clamp-2">
                                        {activity.description}
                                      </p>
                                    )}
                                    {activity.url && (
                                      <a
                                        href={activity.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1 text-sm text-azure-600 hover:text-azure-700 font-medium mt-2"
                                      >
                                        Learn More
                                        <ExternalLink className="w-3 h-3" />
                                      </a>
                                    )}
                                  </div>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Total Cost Estimate */}
                      {metadata.trip_activities.some(a => a.cost_estimate > 0) && (
                        <div className="bg-gold-50 border border-gold-200 rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-stone-700">Estimated Activity Costs:</span>
                            <span className="text-lg font-bold text-gold-900">
                              ${
                                metadata.trip_activities.reduce((sum, activity) => 
                                  sum + (Number(activity.cost_estimate) || 0), 0
                                )
                              } total
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Ticket className="w-16 h-16 mx-auto mb-4 text-stone-300" />
                      <p className="text-lg text-stone-600 mb-2">No activities planned yet</p>
                      <p className="text-sm text-stone-500">Add activities when editing the trip</p>
                    </div>
                  )}
                </div>
              )}
              
              {activeTab === 'budget' && (
                <div>
                  {metadata.budget_breakdown ? (
                    <TripBudgetCalculator
                      initialBudget={metadata.budget_breakdown}
                      tripDuration={5} // Calculate from duration_category
                      numberOfPeople={6} // Excluding test account
                      onBudgetUpdate={(budget) => {
                        // Handle budget update
                      }}
                    />
                  ) : (
                    <div className="text-center py-8 text-stone-500">
                      <DollarSign className="w-12 h-12 mx-auto mb-3 text-stone-300" />
                      <p>No budget created yet</p>
                      <button
                        onClick={() => onEdit?.(trip)}
                        className="mt-3 px-4 py-2 bg-gold-500 text-white rounded-lg hover:bg-gold-600 transition-colors"
                      >
                        Create Budget
                      </button>
                    </div>
                  )}
                </div>
              )}
              
              {activeTab === 'packing' && (
                <div className="space-y-4">
                  {relatedPackingLists.length > 0 ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-semibold text-stone-900">Related Packing Lists</h4>
                        <button
                          onClick={handleCreatePackingList}
                          className="px-4 py-2 bg-gold-500 text-white rounded-lg hover:bg-gold-600 transition-colors text-sm inline-flex items-center gap-2"
                        >
                          <Plus className="w-4 h-4" />
                          New List
                        </button>
                      </div>
                      
                      {relatedPackingLists.map(list => {
                        const packedCount = list.items?.filter(item => item.is_packed).length || 0;
                        const totalCount = list.items?.length || 0;
                        const progress = totalCount > 0 ? (packedCount / totalCount) * 100 : 0;
                        
                        return (
                          <div
                            key={list.id}
                            className="bg-stone-50 rounded-lg p-4 hover:bg-stone-100 transition-colors cursor-pointer"
                            onClick={() => navigate(`/packing/${list.id}`)}
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div>
                                <h5 className="font-semibold text-stone-900">{list.name}</h5>
                                <p className="text-sm text-stone-600 mt-1">
                                  {list.destination || trip.location}
                                </p>
                              </div>
                              <ExternalLink className="w-4 h-4 text-stone-400" />
                            </div>
                            
                            {list.start_date && (
                              <div className="text-sm text-stone-600 mb-3 flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {format(new Date(list.start_date), 'MMM d, yyyy')}
                              </div>
                            )}
                            
                            <div className="space-y-2">
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-stone-600">Packing Progress</span>
                                <span className="font-medium">{packedCount}/{totalCount} items</span>
                              </div>
                              <div className="w-full bg-stone-200 rounded-full h-2">
                                <div
                                  className="bg-emerald-500 h-2 rounded-full transition-all"
                                  style={{ width: `${progress}%` }}
                                />
                              </div>
                            </div>
                            
                            {list.collaborators?.length > 0 && (
                              <div className="mt-3 flex items-center gap-1">
                                <Users className="w-4 h-4 text-stone-400" />
                                <span className="text-sm text-stone-600">
                                  Shared with {list.collaborators.length} {list.collaborators.length === 1 ? 'person' : 'people'}
                                </span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Package className="w-12 h-12 mx-auto mb-3 text-stone-300" />
                      <p className="text-stone-500 mb-4">No packing lists created yet</p>
                      <button
                        onClick={handleCreatePackingList}
                        className="px-6 py-3 bg-gold-500 text-white rounded-lg hover:bg-gold-600 transition-colors inline-flex items-center gap-2"
                      >
                        <Plus className="w-5 h-5" />
                        Create Packing List
                      </button>
                      <p className="text-sm text-stone-600 mt-2">
                        Create a packing list specifically for this trip
                      </p>
                    </div>
                  )}
                </div>
              )}
              
              {activeTab === 'documents' && (
                <div className="space-y-4">
                  {metadata.documents?.length > 0 ? (
                    metadata.documents.map((doc, index) => (
                      <div key={index} className="flex items-center justify-between p-3 border border-stone-200 rounded-lg">
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-stone-500" />
                          <div>
                            <p className="font-medium">{doc.name}</p>
                            <p className="text-sm text-stone-600">{doc.type}</p>
                          </div>
                        </div>
                        <a
                          href={doc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-azure-600 hover:text-azure-700"
                        >
                          <ExternalLink className="w-5 h-5" />
                        </a>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-stone-500">
                      <FileText className="w-12 h-12 mx-auto mb-3 text-stone-300" />
                      <p>No documents uploaded yet</p>
                      <button className="mt-3 px-4 py-2 bg-gold-500 text-white rounded-lg hover:bg-gold-600 transition-colors">
                        Upload Document
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
            </div>
            
            {/* Footer Actions - Now inside scrollable area */}
            <div className="px-4 py-3 border-t border-stone-200 bg-white">
              <div className="flex gap-2">
                {tripPhase === 'idea' && interestedMembers.length >= 3 && (
                  <button
                    onClick={() => {/* Open voting */}}
                    className="flex-1 py-3 bg-gold-500 text-white rounded-lg hover:bg-gold-600 transition-colors font-semibold flex items-center justify-center gap-2"
                  >
                    <Vote className="w-5 h-5" />
                    Open for Family Voting
                  </button>
                )}
                
                {tripPhase === 'planning' && (
                  <button
                    onClick={() => {/* Set dates */}}
                    className="flex-1 py-3 bg-violet-500 text-white rounded-lg hover:bg-violet-600 transition-colors font-semibold flex items-center justify-center gap-2"
                  >
                    <Calendar className="w-5 h-5" />
                    Set Trip Dates
                  </button>
                )}
                
                {tripPhase === 'booked' && (
                  <button
                    onClick={handleCreatePackingList}
                    className="flex-1 py-3 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors font-semibold flex items-center justify-center gap-2"
                  >
                    <Package className="w-5 h-5" />
                    Start Packing
                  </button>
                )}
                
                {tripPhase === 'completed' && (
                  <button
                    onClick={() => {/* Add memories */}}
                    className="flex-1 py-3 bg-azure-500 text-white rounded-lg hover:bg-azure-600 transition-colors font-semibold flex items-center justify-center gap-2"
                  >
                    <Camera className="w-5 h-5" />
                    Add Trip Photos
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Lightbox for full-screen images */}
      {showLightbox && images.length > 0 && (
          <div className="fixed inset-0 z-[60] bg-black flex items-center justify-center" onClick={() => setShowLightbox(false)}>
            <button
              onClick={(e) => { e.stopPropagation(); setShowLightbox(false); }}
              className="absolute top-4 right-4 p-2 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-colors text-white"
            >
              <X className="w-6 h-6" />
            </button>
            
            <img
              src={images[lightboxIndex]}
              alt={`${trip.title} ${lightboxIndex + 1}`}
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            
            {images.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setLightboxIndex((prev) => prev === 0 ? images.length - 1 : prev - 1);
                  }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-colors text-white"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setLightboxIndex((prev) => (prev + 1) % images.length);
                  }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-colors text-white"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
                
                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2">
                  {images.map((_, index) => (
                    <button
                      key={index}
                      onClick={(e) => {
                        e.stopPropagation();
                        setLightboxIndex(index);
                      }}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        index === lightboxIndex ? 'bg-white' : 'bg-white/50'
                      }`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}